import { useEffect, useState } from 'react';
import { FileCheck, Plus, Trash2, Loader2, AlertCircle, Copy } from 'lucide-react';
import { templateApi } from '../services/api';

import { toast, confirmDialog } from '../stores/feedbackStore';

const typeLabels: Record<string, string> = {
  bao_cao: 'Báo cáo',
  quyet_dinh: 'Quyết định',
  to_trinh: 'Tờ trình',
  cong_van: 'Công văn',
  hop_dong: 'Hợp đồng',
  khac: 'Khác',
};

interface Template {
  id: string;
  name: string;
  description: string | null;
  type: string;
  pages_count: number;
  workflow_type: string;
  is_public: boolean;
  created_at: string;
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'cong_van', workflow_type: 'sequential' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await templateApi.list({ limit: 50 });
      setTemplates(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tải danh sách mẫu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await templateApi.create({
        name: form.name,
        description: form.description || undefined,
        type: form.type,
        workflow_type: form.workflow_type as 'sequential' | 'parallel',
      });
      setShowCreate(false);
      setForm({ name: '', description: '', type: 'cong_van', workflow_type: 'sequential' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tạo mẫu');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirmDialog({
      title: 'Xóa mẫu tài liệu',
      message: 'Bạn có chắc chắn muốn xóa mẫu tài liệu này?',
      confirmText: 'Xóa mẫu',
      cancelText: 'Hủy',
      type: 'danger',
    });
    if (!ok) return;

    try {
      await templateApi.delete(id);
      toast.success('Đã xóa mẫu tài liệu thành công');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể xóa mẫu');
    }
  };

  return (
    <div className="page-container">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Mẫu tài liệu</h1>
          <p className="page-subtitle">Tạo tài liệu nhanh từ các mẫu có sẵn</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Tạo mẫu
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3 text-danger-700">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="card p-10 text-center text-gray-500">Đang tải...</div>
      ) : templates.length === 0 ? (
        <div className="card p-12 text-center">
          <FileCheck className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Chưa có mẫu nào</h3>
          <p className="text-gray-500 mb-6">Tạo mẫu để tái sử dụng cấu hình ký cho nhiều tài liệu.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex">
            <Plus className="w-4 h-4 mr-2" /> Tạo mẫu đầu tiên
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="card-hover p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-primary-600" />
                </div>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-danger-600 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{t.name}</h3>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{t.description || 'Không có mô tả'}</p>
              <div className="flex flex-wrap gap-2">
                <span className="badge badge-primary">{typeLabels[t.type] || t.type}</span>
                <span className="badge badge-gray">{t.workflow_type === 'sequential' ? 'Tuần tự' : 'Song song'}</span>
                {t.is_public && <span className="badge badge-success">Công khai</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Tạo mẫu tài liệu</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="label">Tên mẫu <span className="text-danger-500">*</span></label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input"
                  placeholder="Ví dụ: Công văn mẫu 2024"
                />
              </div>
              <div>
                <label className="label">Mô tả</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Loại</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
                    {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Quy trình</label>
                  <select value={form.workflow_type} onChange={(e) => setForm({ ...form, workflow_type: e.target.value })} className="input">
                    <option value="sequential">Tuần tự</option>
                    <option value="parallel">Song song</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-outline flex-1">Hủy</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tạo mẫu'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}